/**
 * "Saved searches" beside the Search button (contract §2 presets): apply one,
 * save the current form as a new one, rename, delete. Each shows who made it
 * and when it was last used, so stale ones are easy to spot.
 */
import { useEffect, useRef, useState, type FormEvent, type JSX } from 'react';
import { Bookmark, ChevronDown, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PlatformIcon } from '@/components/PlatformIcon';
import type { SavedSearch } from '../api/types';
import { FieldError, relativeTime } from './controls';
import { useDeleteSavedSearch, useSavedSearches, useSaveSearch, useUpdateSavedSearch } from './hooks';
import { ParamsLabel } from './ParamsLabel';

export function SavedSearchesMenu({
  onApply,
  currentParams,
  suggestedName,
  canSave,
}: {
  onApply: (s: SavedSearch) => void;
  /** What "Save current as…" stores. */
  currentParams: () => Record<string, unknown>;
  suggestedName: string;
  canSave: boolean;
}): JSX.Element {
  const list = useSavedSearches();
  const remove = useDeleteSavedSearch();
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<{ kind: 'save' } | { kind: 'rename'; search: SavedSearch } | null>(null);
  const [deleting, setDeleting] = useState<SavedSearch | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(ev: MouseEvent) {
      if (ref.current && !ref.current.contains(ev.target as Node)) setOpen(false);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const searches = Array.isArray(list.data) ? list.data : [];

  return (
    <div ref={ref} className="relative">
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Saved searches"
        title="Saved searches"
        className="px-3"
      >
        <Bookmark className="h-4 w-4" />
        <span className="hidden sm:inline">Saved searches</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </Button>
      {open && (
        <div
          role="dialog"
          aria-label="Saved searches"
          className="absolute bottom-full right-0 z-40 mb-2 w-[min(24rem,calc(100vw-2rem))] rounded-xl border border-border bg-popover p-1.5 shadow-overlay"
        >
          {list.isLoading ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">Loading…</p>
          ) : searches.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">
              No saved searches yet. Save one to re-run it in any folder.
            </p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {searches.map((s) => (
                <li key={s.id} className="group flex items-center gap-1 rounded-lg hover:bg-accent/60">
                  <button
                    type="button"
                    onClick={() => {
                      onApply(s);
                      setOpen(false);
                    }}
                    className="min-w-0 flex-1 px-2.5 py-2 text-left"
                    title="Load into the form"
                  >
                    <span className="flex items-center gap-1.5">
                      {s.platform && <PlatformIcon platform={s.platform} size={14} className="shrink-0 text-muted-foreground" />}
                      <span className="truncate text-sm font-medium">{s.name}</span>
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                      <ParamsLabel params={s.params} />
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {s.createdByName ? `By ${s.createdByName}` : 'Unknown author'} · last used {relativeTime(s.lastUsedAt)}
                    </span>
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    aria-label={`Rename ${s.name}`}
                    title="Rename"
                    onClick={() => {
                      setDialog({ kind: 'rename', search: s });
                      setOpen(false);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 hover:text-destructive-strong"
                    aria-label={`Delete ${s.name}`}
                    title="Delete"
                    onClick={() => {
                      setDeleting(s);
                      setOpen(false);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-1 border-t border-border pt-1">
            <button
              type="button"
              disabled={!canSave}
              onClick={() => {
                setDialog({ kind: 'save' });
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium hover:bg-accent/60 disabled:cursor-not-allowed disabled:opacity-50"
              title={canSave ? undefined : 'Add a search term first'}
            >
              <Plus className="h-4 w-4 text-muted-foreground" />
              Save current search as…
            </button>
          </div>
        </div>
      )}

      <NameDialog
        state={dialog}
        suggestedName={suggestedName}
        currentParams={currentParams}
        onClose={() => setDialog(null)}
      />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete this saved search?"
        description={deleting ? `"${deleting.name}" is removed for the whole team. Searches already run are unaffected.` : undefined}
        tone="destructive"
        confirmLabel="Delete"
        pending={remove.isPending}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
        }}
      />
    </div>
  );
}

function NameDialog({
  state,
  suggestedName,
  currentParams,
  onClose,
}: {
  state: { kind: 'save' } | { kind: 'rename'; search: SavedSearch } | null;
  suggestedName: string;
  currentParams: () => Record<string, unknown>;
  onClose: () => void;
}): JSX.Element {
  const save = useSaveSearch();
  const update = useUpdateSavedSearch();
  const [name, setName] = useState('');
  const [alsoParams, setAlsoParams] = useState(false);

  useEffect(() => {
    if (!state) return;
    setName(state.kind === 'rename' ? state.search.name : suggestedName);
    setAlsoParams(false);
    save.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const pending = save.isPending || update.isPending;
  const error = (save.error ?? update.error) as Error | null;

  function submit(ev: FormEvent) {
    ev.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || !state) return;
    if (state.kind === 'save') {
      save.mutate({ name: trimmed, params: currentParams() }, { onSuccess: onClose });
    } else {
      update.mutate(
        { id: state.search.id, patch: { name: trimmed, ...(alsoParams ? { params: currentParams() } : {}) } },
        { onSuccess: onClose },
      );
    }
  }

  return (
    <Dialog open={state !== null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{state?.kind === 'rename' ? 'Rename saved search' : 'Save this search'}</DialogTitle>
            <DialogDescription>
              {state?.kind === 'rename'
                ? 'Saved searches are shared with the whole team.'
                : 'Saves the platform, query and every filter. Anyone on the team can load it into any folder.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <label htmlFor="saved-search-name" className="text-sm font-medium">
              Name
            </label>
            <Input id="saved-search-name" value={name} onChange={(ev) => setName(ev.target.value)} autoFocus maxLength={120} />
            {state?.kind === 'rename' && (
              <label className="mt-1 flex items-center gap-2.5 text-sm text-foreground/85">
                <input
                  type="checkbox"
                  checked={alsoParams}
                  onChange={(ev) => setAlsoParams(ev.target.checked)}
                  className="h-4 w-4 rounded border-input accent-[var(--brand)]"
                />
                Also replace its settings with the current form
              </label>
            )}
            <FieldError message={error?.message} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending || !name.trim()}>
              {state?.kind === 'rename' ? 'Save name' : 'Save search'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
